import React, { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { useWallet } from '../utils/walletContext';
import { useClaims } from '../utils/claimContext';
import {
  getContract,
  getCaseDetails,
  formatAddress,
  BASE_MAINNET
} from '../utils/contractUtils';
import './MyClaims.css';

/**
 * MyClaims Component
 * 
 * Displays all claims associated with the connected wallet address
 * Allows users to view their claims as plaintiff, defendant, or owner
 */
const MyClaims = () => {
  const { account, isConnected, connectWallet } = useWallet();
  const { refreshClaims } = useClaims();
  
  // State variables
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [myClaims, setMyClaims] = useState([]);
  const [viewType, setViewType] = useState('all'); // 'all', 'plaintiff', 'defendant', 'owner'
  const [activeClaim, setActiveClaim] = useState(null);
  const [claimMetadata, setClaimMetadata] = useState(null);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [totalClaims, setTotalClaims] = useState(0);

  // No action needed - we've replaced this with queryClaimsByType
  
  // Function to load claim metadata from IPFS - also memoize this for useEffect dependency
  const loadClaimMetadata = useCallback(async (uri) => {
    if (!uri) return;
    
    setMetadataLoading(true);
    setClaimMetadata(null);
    
    try {
      // Extract CID from URI
      let cid;
      if (uri.startsWith('ipfs://')) {
        cid = uri.replace('ipfs://', '');
      } else if (uri.includes('/ipfs/')) {
        cid = uri.split('/ipfs/')[1];
      } else {
        cid = uri;
      }
      
      // Use Infura IPFS gateway directly
      const infuraUrl = `https://freeweb3.infura-ipfs.io/ipfs/${cid}`;
      
      // Fetch metadata from IPFS using the Infura gateway
      const response = await fetch(infuraUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch from IPFS: ${response.statusText}`);
      }
      
      const data = await response.json();
      setClaimMetadata(data);
    } catch (err) {
      console.error("Error loading claim metadata:", err);
      setClaimMetadata({ error: "Failed to load metadata from IPFS" });
    } finally {
      setMetadataLoading(false);
    }
  }, []);

  // Function to handle claim selection
  const handleSelectClaim = (claim) => {
    setActiveClaim(claim);
  };

  // Function to query the blockchain for claims based on filter type
  const queryClaimsByType = useCallback(async () => {
    setLoading(true);
    setError('');
    setMyClaims([]);
    
    try {
      await getContract();
      
      // For demo purposes, we'll estimate the total
      const MAX_CASE_ID = 50;
      setTotalClaims(MAX_CASE_ID);
      
      // For demo purposes, we'll check the first 50 cases
      const casesToCheck = Array.from({ length: MAX_CASE_ID }, (_, i) => i);
      
      // Prepare promises for batch processing
      const casePromises = casesToCheck.map(async (caseId) => {
        try {
          return await getCaseDetails(caseId);
        } catch (err) {
          // Skip cases that don't exist
          return null;
        }
      });
      
      // Execute all promises
      const allCases = await Promise.all(casePromises);
      
      // Filter cases based on view type and connected account
      const filteredCases = allCases.filter(caseData => {
        if (!caseData) return false;
        
        const isPlaintiff = caseData.plaintiff.toLowerCase() === account.toLowerCase();
        const isDefendant = caseData.defendant.toLowerCase() === account.toLowerCase();
        const isOwner = caseData.owner.toLowerCase() === account.toLowerCase();
        
        switch (viewType) {
          case 'plaintiff':
            return isPlaintiff;
          case 'defendant':
            return isDefendant;
          case 'owner':
            return isOwner;
          case 'all':
          default:
            return isPlaintiff || isDefendant || isOwner;
        }
      });
      
      // Sort cases by ID (descending - newest first)
      const sortedCases = filteredCases.sort((a, b) => b.caseId - a.caseId);
      
      setMyClaims(sortedCases);
      
      // Set first claim as active if available
      if (sortedCases.length > 0 && !activeClaim) {
        setActiveClaim(sortedCases[0]);
      }
      
      // Refresh the global claim count
      refreshClaims();
    } catch (err) {
      console.error("Error loading claims:", err);
      setError("Failed to load your claims. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [account, viewType, activeClaim, refreshClaims]);

  // Load user claims when account changes
  useEffect(() => {
    if (isConnected && account) {
      queryClaimsByType();
    } else {
      setMyClaims([]);
      setTotalClaims(0);
      setActiveClaim(null);
      setClaimMetadata(null);
    }
  }, [isConnected, account, viewType, queryClaimsByType]);

  // Load claim metadata when active claim changes
  useEffect(() => {
    if (activeClaim) {
      loadClaimMetadata(activeClaim.uri);
    } else {
      setClaimMetadata(null);
    }
  }, [activeClaim, loadClaimMetadata]);
  
  // Format date from ISO string
  const formatDate = (isoString) => {
    if (!isoString) return 'Unknown date';
    
    try {
      const date = new Date(isoString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch (err) {
      return isoString;
    }
  };

  // Get role label for a claim
  const getRoleLabel = (claim) => {
    if (!claim || !account) return [];
    
    const roles = [];
    
    if (claim.plaintiff.toLowerCase() === account.toLowerCase()) {
      roles.push({ label: 'Plaintiff', color: 'bg-blue-100 text-blue-800' });
    }
    
    if (claim.defendant.toLowerCase() === account.toLowerCase() && 
        claim.defendant !== ethers.ZeroAddress) {
      roles.push({ label: 'Defendant', color: 'bg-orange-100 text-orange-800' });
    }
    
    if (claim.owner.toLowerCase() === account.toLowerCase()) {
      roles.push({ label: 'Owner', color: 'bg-green-100 text-green-800' });
    }
    
    return roles;
  };

  // Get a badge class based on status
  const getStatusBadgeClass = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    
    status = status.toLowerCase();
    
    if (status.includes('filed')) return 'bg-blue-100 text-blue-800';
    if (status.includes('pending')) return 'bg-yellow-100 text-yellow-800';
    if (status.includes('scheduled')) return 'bg-purple-100 text-purple-800';
    if (status.includes('progress')) return 'bg-indigo-100 text-indigo-800';
    if (status.includes('judgment') || status.includes('decided')) return 'bg-green-100 text-green-800';
    if (status.includes('closed')) return 'bg-gray-100 text-gray-800';
    if (status.includes('contested')) return 'bg-red-100 text-red-800';
    
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="my-claims-container">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">My Claims</h2>
        {isConnected && (
          <button 
            onClick={queryClaimsByType}
            disabled={loading}
            className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center text-sm"
          >
            {loading ? (
              <span className="inline-block mr-2 h-4 w-4 border-t-2 border-b-2 border-white rounded-full animate-spin"></span>
            ) : (
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
            )}
            Refresh Claims
          </button>
        )}
      </div>
      
      {!isConnected ? (
        <div className="my-claims-connect-prompt">
          <p className="text-gray-600 mb-4">Connect your wallet to view your claims.</p>
          <button 
            onClick={connectWallet}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Connect Wallet
          </button>
        </div>
      ) : (
        <div className="my-claims-content">
          {/* Filter tabs */}
          <div className="my-claims-filters mb-6">
            <div className="flex border-b border-gray-200">
              <button 
                className={`px-4 py-2 ${viewType === 'all' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => {
                  setViewType('all');
                  setActiveClaim(null); // Reset active claim when changing view
                }}
              >
                All Claims
              </button>
              <button 
                className={`px-4 py-2 ${viewType === 'plaintiff' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => {
                  setViewType('plaintiff');
                  setActiveClaim(null); // Reset active claim when changing view
                }}
              >
                As Plaintiff
              </button>
              <button 
                className={`px-4 py-2 ${viewType === 'defendant' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => {
                  setViewType('defendant');
                  setActiveClaim(null); // Reset active claim when changing view
                }}
              >
                As Defendant
              </button>
              <button 
                className={`px-4 py-2 ${viewType === 'owner' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => {
                  setViewType('owner');
                  setActiveClaim(null); // Reset active claim when changing view
                }}
              >
                Owned
              </button>
            </div>
          </div>

          {/* Stats summary */}
          <div className="claims-stats grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="stat-card bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-500 dark:text-gray-400">Total System Claims</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{totalClaims}</div>
            </div>
            <div className="stat-card bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-500 dark:text-gray-400">Your Claims</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{myClaims.length}</div>
            </div>
            <div className="stat-card bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-500 dark:text-gray-400">As Plaintiff</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {myClaims.filter(c => c.plaintiff.toLowerCase() === account.toLowerCase()).length}
              </div>
            </div>
            <div className="stat-card bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-500 dark:text-gray-400">As Defendant</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {myClaims.filter(c => c.defendant.toLowerCase() === account.toLowerCase() &&
                                      c.defendant !== ethers.ZeroAddress).length}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="loading-spinner flex justify-center my-8">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <div className="error-message bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          ) : myClaims.length === 0 ? (
            <div className="no-claims bg-gray-50 p-8 rounded-lg text-center">
              <div className="text-gray-400 mb-2">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-1">No Claims Found</h3>
              <p className="text-gray-500">
                You don't have any {viewType !== 'all' ? `claims as ${viewType}` : 'claims'} yet.
              </p>
              <button 
                onClick={() => window.location.href = '/file-claim'}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                File a New Claim
              </button>
            </div>
          ) : (
            <div className="claims-dashboard grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Claims list */}
              <div className="claims-list md:col-span-1 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                  <h3 className="font-semibold">Your Claims ({myClaims.length})</h3>
                </div>
                <div className="claims-list-items max-h-[500px] overflow-y-auto">
                  {myClaims.map(claim => (
                    <div 
                      key={claim.caseId} 
                      className={`claim-item p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors ${
                        activeClaim && activeClaim.caseId === claim.caseId ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                      }`}
                      onClick={() => handleSelectClaim(claim)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium">Case #{claim.caseId}</div>
                          <div className={`inline-block px-2 py-1 text-xs rounded-full mt-1 ${getStatusBadgeClass(claim.status)}`}>
                            {claim.status}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {getRoleLabel(claim).map((role, idx) => (
                            <span 
                              key={idx} 
                              className={`px-2 py-1 text-xs rounded-full ${role.color}`}
                            >
                              {role.label}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Claim details */}
              <div className="claim-details md:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200">
                {activeClaim ? (
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-6">
                      <h3 className="text-xl font-bold">Case #{activeClaim.caseId}</h3>
                      <div className={`px-3 py-1 rounded-full text-sm ${getStatusBadgeClass(activeClaim.status)}`}>
                        {activeClaim.status}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold mb-2">Plaintiff</h4>
                        <a 
                          href={`${BASE_MAINNET.blockExplorer}/address/${activeClaim.plaintiff}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline break-all"
                        >
                          {activeClaim.plaintiff === account.toLowerCase() ? 'You' : formatAddress(activeClaim.plaintiff, 8)}
                        </a>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold mb-2">Defendant</h4>
                        {activeClaim.defendant === ethers.ZeroAddress ? (
                          <span className="text-gray-500">Not assigned yet</span>
                        ) : (
                          <a 
                            href={`${BASE_MAINNET.blockExplorer}/address/${activeClaim.defendant}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline break-all"
                          >
                            {activeClaim.defendant.toLowerCase() === account.toLowerCase() ? 'You' : formatAddress(activeClaim.defendant, 8)}
                          </a>
                        )}
                      </div>
                    </div>

                    <div className="mb-6">
                      <h4 className="font-semibold mb-2">IPFS Metadata</h4>
                      <div className="bg-gray-50 p-4 rounded-lg overflow-hidden">
                        <p className="break-all mb-2">
                          {(() => {
                            // Extract CID from URI
                            let cid = activeClaim.uri;
                            if (activeClaim.uri.startsWith('ipfs://')) {
                              cid = activeClaim.uri.replace('ipfs://', '');
                            } else if (activeClaim.uri.includes('/ipfs/')) {
                              cid = activeClaim.uri.split('/ipfs/')[1];
                            }
                            
                            // Use Infura IPFS gateway URL
                            const displayUrl = `https://freeweb3.infura-ipfs.io/ipfs/${cid}`;
                            
                            return (
                              <a 
                                href={displayUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                {displayUrl}
                              </a>
                            );
                          })()}
                        </p>
                        
                        {metadataLoading ? (
                          <div className="flex justify-center my-4">
                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                          </div>
                        ) : claimMetadata ? (
                          <div className="metadata-content mt-4 border-t border-gray-200 pt-4">
                            {claimMetadata.error ? (
                              <div className="text-red-500">{claimMetadata.error}</div>
                            ) : (
                              <div>
                                {claimMetadata.description && (
                                  <div className="mb-4">
                                    <h5 className="font-medium text-sm text-gray-500 mb-1">Description</h5>
                                    <p>{claimMetadata.description}</p>
                                  </div>
                                )}
                                
                                {claimMetadata.claimAmount && (
                                  <div className="mb-4">
                                    <h5 className="font-medium text-sm text-gray-500 mb-1">Claim Amount</h5>
                                    <p className="font-semibold">{claimMetadata.claimAmount} ETH</p>
                                  </div>
                                )}
                                
                                {claimMetadata.timestamp && (
                                  <div className="mb-4">
                                    <h5 className="font-medium text-sm text-gray-500 mb-1">Filed Date</h5>
                                    <p>{formatDate(claimMetadata.timestamp)}</p>
                                  </div>
                                )}
                                
                                {/* Display any additional metadata fields */}
                                <div className="grid grid-cols-2 gap-4">
                                  {Object.entries(claimMetadata)
                                    .filter(([key]) => !['description', 'claimAmount', 'timestamp', 'status'].includes(key))
                                    .map(([key, value]) => (
                                      <div key={key} className="metadata-item">
                                        <h5 className="font-medium text-sm text-gray-500 mb-1 capitalize">
                                          {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ')}
                                        </h5>
                                        <p className="truncate">
                                          {typeof value === 'object' ? JSON.stringify(value) : value.toString()}
                                        </p>
                                      </div>
                                    ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-gray-500">No metadata available</div>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-between">
                      <a 
                        href={`https://basescan.org/token/0x4B2cF77Dbd063299536AB916Fb1445bD3267162F?a=${activeClaim.caseId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline text-sm"
                      >
                        View on BlockExplorer
                      </a>
                      {activeClaim.plaintiff.toLowerCase() === account.toLowerCase() && (
                        <a 
                          href="/register-case" 
                          className="text-blue-600 hover:underline text-sm"
                        >
                          Manage Case
                        </a>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <p className="text-gray-500">
                      Select a claim to view its details
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MyClaims;